<script setup lang="ts">
import { h, nextTick, ref } from 'vue';
import { NTag, type SelectOption, type SelectRenderLabel, type SelectRenderTag } from 'naive-ui';
import type { ReviewProjectFileInDb, ReviewProjectTagInDb } from '@/api';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { tagChipColor } from '@/utils/tagColors';
import { IconAddCircle } from '@/components/design/icons';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  projectUid: string;
  file: ReviewProjectFileInDb;
}>();

const emit = defineEmits<{
  (e: 'updated', file: ReviewProjectFileInDb): void;
}>();

const message = useGaiaToast();

// ==================== Chips ====================
const truncateStyle = {
  maxWidth: '56px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
} as const;

// ==================== Editor ====================
// Values produced by the select's tag mode for names that don't exist yet.
const CREATE_PREFIX = 'create:';

// Options carry their tag so the dropdown and selected chips can be colored.
interface TagSelectOption extends SelectOption {
  tag?: ReviewProjectTagInDb;
}

const showEditor = ref(false);
const selectRef = ref<{ focus: () => void } | null>(null);
const options = ref<TagSelectOption[]>([]);
const optionsLoading = ref(false);
const selectedUids = ref<string[]>([]);
const saving = ref(false);

// Last server-confirmed tag set, to revert to on save failure.
let confirmedUids: string[] = [];
// Saves are serialized so rapid toggling can't race; the PUT replaces the
// full tag set, so the last save in the chain wins deterministically.
let saveChain: Promise<void> = Promise.resolve();

function toOption(tag: ReviewProjectTagInDb): TagSelectOption {
  return { label: tag.name, value: tag.uid, tag };
}

function onEditorShow(show: boolean): void {
  showEditor.value = show;
  if (!show) return;
  selectedUids.value = props.file.tags.map(tag => tag.uid);
  confirmedUids = [...selectedUids.value];
  // Seed with the file's own tags so the selected chips render colored
  // (not as raw uids) while the full list loads.
  options.value = props.file.tags.map(toOption);
  void loadOptions();
  void nextTick(() => selectRef.value?.focus());
}

async function loadOptions(): Promise<void> {
  optionsLoading.value = true;
  try {
    const result = await ReviewProjectService.searchTags(props.projectUid, { pageSize: 50 });
    options.value = result.results.map(toOption);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load tags.');
  } finally {
    optionsLoading.value = false;
  }
}

const renderOptionLabel: SelectRenderLabel = option =>
  h(
    NTag,
    {
      size: 'small',
      round: true,
      color: tagChipColor((option as TagSelectOption).tag?.color),
    },
    { default: () => (typeof option.label === 'string' ? option.label : '') }
  );

const renderSelectedTag: SelectRenderTag = ({ option, handleClose }) =>
  h(
    NTag,
    {
      size: 'small',
      round: true,
      closable: true,
      color: tagChipColor((option as TagSelectOption).tag?.color),
      onClose: (e: MouseEvent): void => {
        e.stopPropagation();
        handleClose();
      },
    },
    { default: () => String(option.label ?? '') }
  );

function handleCreate(label: string): SelectOption {
  const name = label.trim();
  return { label: name, value: CREATE_PREFIX + name };
}

function findTagUidByName(name: string): string | null {
  const lower = name.toLowerCase();
  const match = options.value.find(
    option => typeof option.label === 'string' && option.label.toLowerCase() === lower
  );
  return match ? String(match.value) : null;
}

function handleValueUpdate(value: (string | number)[] | string | number | null): void {
  const uids = Array.isArray(value) ? value.map(String) : [];
  selectedUids.value = uids;
  saveChain = saveChain.then(() => resolveAndPersist(uids));
}

async function resolveAndPersist(value: string[]): Promise<void> {
  saving.value = true;
  try {
    const uids: string[] = [];
    for (const entry of value) {
      if (!entry.startsWith(CREATE_PREFIX)) {
        if (!uids.includes(entry)) uids.push(entry);
        continue;
      }
      const name = entry.slice(CREATE_PREFIX.length).trim();
      if (name.length === 0) continue;
      // Reuse an existing tag typed by name instead of creating a duplicate.
      let uid = findTagUidByName(name);
      if (!uid) {
        // No color: the backend default applies; colors are edited in settings.
        const tag = await ReviewProjectService.createTag(props.projectUid, { name });
        options.value = [...options.value, toOption(tag)];
        uid = tag.uid;
      }
      if (!uids.includes(uid)) uids.push(uid);
    }
    const updated = await ReviewProjectService.updateFile(props.projectUid, props.file.uid, {
      tag_uids: uids,
    });
    confirmedUids = updated.tags.map(tag => tag.uid);
    selectedUids.value = [...confirmedUids];
    emit('updated', updated);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update tags.');
    selectedUids.value = [...confirmedUids];
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <n-space :size="4" :wrap="false" align="center">
    <n-tag v-if="file.tags.length > 0" size="small" round :color="tagChipColor(file.tags[0].color)">
      <span :style="truncateStyle" :title="file.tags[0].name">{{ file.tags[0].name }}</span>
    </n-tag>
    <n-popover v-if="file.tags.length > 1" trigger="hover" placement="top">
      <template #trigger>
        <n-tag size="small" round>+{{ file.tags.length - 1 }}</n-tag>
      </template>
      <n-space :size="4" style="max-width: 320px">
        <n-tag
          v-for="tag in file.tags"
          :key="tag.uid"
          size="small"
          round
          :color="tagChipColor(tag.color)"
        >
          {{ tag.name }}
        </n-tag>
      </n-space>
    </n-popover>
    <n-popover
      trigger="click"
      placement="bottom-start"
      :show="showEditor"
      @update:show="onEditorShow"
    >
      <template #trigger>
        <n-button quaternary circle size="tiny" @click.stop>
          <template #icon>
            <n-icon :size="16"><icon-add-circle /></n-icon>
          </template>
        </n-button>
      </template>
      <!-- :to="false" keeps the dropdown inside the popover, so picking an
           option doesn't register as an outside click that closes the editor. -->
      <gaia-select
        ref="selectRef"
        multiple
        filterable
        tag
        size="small"
        :to="false"
        :value="selectedUids"
        :options="options"
        :loading="optionsLoading || saving"
        :on-create="handleCreate"
        :render-label="renderOptionLabel"
        :render-tag="renderSelectedTag"
        placeholder="Search or create tags"
        style="width: 260px"
        @update:value="handleValueUpdate"
      />
    </n-popover>
  </n-space>
</template>
